import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Dropdown from 'react-bootstrap/Dropdown';
import Image from 'react-bootstrap/Image';
import { API_BASE_URL } from '../config/api';
import { getLoggedInUserData } from '../services/authService';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../services/workflowService';
import { buildFallbackUser, getDecryptedData, getUserType, logoutSession } from '../utils/authStorage';
import { getPageTitle, ROLE_LABELS } from '../config/navigation';
import { resolveNotificationNavigationPath } from '../utils/profileNavigation';
import { useLayout } from './AppLayout';

function AppHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setMobileSidebarOpen } = useLayout();
  const [user, setUser] = useState(buildFallbackUser());
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const userType = getUserType();
  const userId = getDecryptedData('Id');
  const pageTitle = getPageTitle(location.pathname);

  const applyNotificationPayload = (data) => {
    setNotifications(data.notifications || []);
    const todayUnread = typeof data.todayUnreadCount === 'number'
      ? data.todayUnreadCount
      : (data.unreadCount || 0);
    setUnreadCount(todayUnread);
  };

  const resolveTodayUnreadFromMarkResponse = (result) => {
    if (typeof result.todayUnreadCount === 'number') return result.todayUnreadCount;
    if (typeof result.persistedUnreadCount === 'number') return result.persistedUnreadCount;
    return null;
  };

  useEffect(() => {
    const email = getDecryptedData('Email');
    if (!email) return;
    getLoggedInUserData(email)
      .then((users) => {
        if (users.length > 0) setUser(users[0]);
      })
      .catch(() => setUser(buildFallbackUser()));
  }, []);

  useEffect(() => {
    if (!userId) return;
    getNotifications(userId)
      .then((data) => {
        applyNotificationPayload(data);
      })
      .catch(() => {
        setNotifications([]);
        setUnreadCount(0);
      });
  }, [userId, location.pathname]);

  const profileSrc = user.ProfilePic ? `${API_BASE_URL}/${user.ProfilePic}` : '/Images/profile.svg';

  const handleNotificationClick = async (item) => {
    const path = resolveNotificationNavigationPath(item, userType);

    if (item?.persisted && item?.unread && userId && item?.id) {
      try {
        const result = await markNotificationRead(userId, item.id);
        if (result.status === 'Success') {
          setNotifications((prev) => prev.map((entry) => (
            entry.id === item.id ? { ...entry, unread: false } : entry
          )));
          const todayUnread = resolveTodayUnreadFromMarkResponse(result);
          if (todayUnread !== null) {
            setUnreadCount(todayUnread);
          } else if (!result.alreadyRead) {
            setUnreadCount((prev) => Math.max(0, prev - 1));
          }
        }
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    if (path) navigate(path);
  };

  const handleMarkAllRead = async () => {
    if (!userId) return;
    if (unreadCount <= 0) return;

    try {
      const result = await markAllNotificationsRead(userId);
      if (result.status === 'Success') {
        setNotifications((prev) => prev.map((entry) => (
          entry.persisted ? { ...entry, unread: false } : entry
        )));
        const todayUnread = resolveTodayUnreadFromMarkResponse(result);
        setUnreadCount(todayUnread !== null ? todayUnread : 0);
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const hasPersistedUnread = unreadCount > 0;

  return (
    <header className="hr-header">
      <div className="hr-header-left">
        <button type="button" className="hr-header-menu-btn" onClick={() => setMobileSidebarOpen(true)} aria-label="Open menu">
          ☰
        </button>
        <div>
          <p className="hr-header-eyebrow">{ROLE_LABELS[userType] || 'User'}</p>
          <h1 className="hr-header-title">{pageTitle}</h1>
        </div>
      </div>

      <div className="hr-header-right">
        <Dropdown align="end">
          <Dropdown.Toggle as="button" className="hr-header-icon-btn hr-notification-btn" aria-label="Notifications">
            🔔
            {unreadCount > 0 && <span className="hr-notification-badge">{unreadCount}</span>}
          </Dropdown.Toggle>
          <Dropdown.Menu className="hr-notification-menu">
            {hasPersistedUnread ? (
              <Dropdown.Item as="button" type="button" onClick={handleMarkAllRead}>
                Mark all as read
              </Dropdown.Item>
            ) : null}
            {hasPersistedUnread ? <Dropdown.Divider /> : null}
            {notifications.length === 0 ? (
              <Dropdown.ItemText className="hr-notification-empty">No notifications right now.</Dropdown.ItemText>
            ) : (
              notifications.map((item) => (
                <Dropdown.Item
                  key={item.id}
                  onClick={() => handleNotificationClick(item)}
                >
                  <strong>{item.title}</strong>
                  <span>{item.message}</span>
                </Dropdown.Item>
              ))
            )}
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown align="end">
          <Dropdown.Toggle as="button" className="hr-profile-toggle">
            <Image src={profileSrc} roundedCircle className="hr-profile-avatar" alt="Profile" />
            <span className="hr-profile-name">{user.EmployeeName}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu className="hr-profile-menu">
            <Dropdown.Item onClick={() => navigate('/Profile')}>My Profile</Dropdown.Item>
            <Dropdown.Item onClick={() => navigate('/settings')}>Settings</Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={logoutSession}>Logout</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </header>
  );
}

export default AppHeader;
